import type { GameAction } from '../game/actions';
import type { GameController } from '../core/GameController';
import {
  parseClientMessage,
  parseHostMessage,
  PROTOCOL_VERSION,
  type ClientMessage,
  type Emote,
  type HostMessage,
  type PlayerInfo,
  type PlayerPose,
} from './protocol';
import type { Connection, HostEndpoint } from './transport';

export const MAX_VISITORS = 5;
const HELLO_TIMEOUT_MS = 10_000;
const STATE_SYNC_MIN_MS = 150;
const STATE_SYNC_MAX_MS = 1000;
const CHAT_WINDOW_MS = 5000;
const CHAT_LIMIT = 5;

/** Presentation hooks a session reports to (implemented by the app). */
export interface SessionListener {
  onPlayers(players: PlayerInfo[]): void;
  onPose(id: string, pose: PlayerPose): void;
  onChat(id: string, text: string): void;
  onEmote(id: string, emote: Emote): void;
  onSystem(text: string): void;
  /** The host endpoint died unexpectedly (host side only). */
  onHostLost?(reason: string): void;
}

interface Visitor {
  connection: Connection;
  info: PlayerInfo | null;
  chatTimes: number[];
}

/**
 * Runs on the farm owner's machine: accepts visitors, applies their actions
 * through the shared rules engine, and streams state/events back to everyone.
 */
export class HostSession {
  private readonly visitors = new Map<string, Visitor>();
  private readonly unsubscribers: Array<() => void> = [];
  private dirty = true;
  private lastSync = 0;
  private closed = false;

  constructor(
    private readonly endpoint: HostEndpoint,
    private readonly game: GameController,
    private readonly self: PlayerInfo,
    private readonly listener: SessionListener,
  ) {
    endpoint.onConnection((connection) => this.accept(connection));
    endpoint.onLost((reason) => {
      if (this.closed) return;
      this.close();
      this.listener.onHostLost?.(reason);
    });
    this.unsubscribers.push(
      game.events.on((events) => this.broadcast({ t: 'events', events })),
      game.changed.on(() => (this.dirty = true)),
    );
  }

  get code(): string {
    return this.endpoint.code;
  }

  players(): PlayerInfo[] {
    const visitors = [...this.visitors.values()].flatMap((v) => (v.info ? [v.info] : []));
    return [this.self, ...visitors];
  }

  /** Call every frame; batches state snapshots to visitors. */
  update(now: number): void {
    if (this.closed) return;
    const elapsed = now - this.lastSync;
    if ((this.dirty && elapsed >= STATE_SYNC_MIN_MS) || elapsed >= STATE_SYNC_MAX_MS) {
      this.dirty = false;
      this.lastSync = now;
      this.broadcast({ t: 'state', state: this.game.state });
    }
  }

  sendPose(pose: PlayerPose): void {
    this.broadcast({ t: 'pose', id: this.self.id, pose });
  }

  sendChat(text: string): void {
    this.broadcast({ t: 'chat', id: this.self.id, text });
  }

  sendEmote(emote: Emote): void {
    this.broadcast({ t: 'emote', id: this.self.id, emote });
  }

  updateSelf(info: Omit<PlayerInfo, 'id'>): void {
    this.self.name = info.name;
    this.self.color = info.color;
    this.publishPlayers();
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.broadcast({ t: 'kick', reason: 'The host closed their farm.' });
    for (const unsubscribe of this.unsubscribers) unsubscribe();
    // Give the goodbye message a moment to flush before tearing down.
    const visitors = [...this.visitors.values()];
    this.visitors.clear();
    setTimeout(() => {
      for (const visitor of visitors) visitor.connection.close();
      this.endpoint.close();
    }, 250);
  }

  private accept(connection: Connection): void {
    if (this.closed) return connection.close();
    if (this.visitors.size >= MAX_VISITORS) {
      connection.send({ t: 'kick', reason: 'This farm is full right now.' } satisfies HostMessage);
      setTimeout(() => connection.close(), 250);
      return;
    }
    const visitor: Visitor = { connection, info: null, chatTimes: [] };
    this.visitors.set(connection.id, visitor);
    const helloTimer = setTimeout(() => !visitor.info && connection.close(), HELLO_TIMEOUT_MS);
    connection.onMessage((raw) => this.handle(visitor, raw));
    connection.onClose(() => {
      clearTimeout(helloTimer);
      if (this.visitors.get(connection.id) !== visitor) return;
      this.visitors.delete(connection.id);
      if (visitor.info) {
        this.listener.onSystem(`${visitor.info.name} went home 👋`);
        this.publishPlayers();
      }
    });
  }

  private handle(visitor: Visitor, raw: unknown): void {
    const message = parseClientMessage(raw);
    if (!message) return;
    const { connection } = visitor;

    if (!visitor.info) {
      if (message.t !== 'hello') return;
      if (message.v !== PROTOCOL_VERSION) {
        connection.send({
          t: 'kick',
          reason: 'Your game version is different — please refresh the page.',
        } satisfies HostMessage);
        setTimeout(() => connection.close(), 250);
        return;
      }
      visitor.info = { id: connection.id, name: message.name, color: message.color };
      this.send(connection, {
        t: 'welcome',
        you: connection.id,
        state: this.game.state,
        players: this.players(),
      });
      this.listener.onSystem(`${message.name} arrived at the farm! 🌼`);
      this.publishPlayers();
      return;
    }

    const id = visitor.info.id;
    switch (message.t) {
      case 'action': {
        const result = this.game.applyAuthoritative(message.action, id);
        if (!result.ok) this.send(connection, { t: 'reject', reason: result.reason });
        break;
      }
      case 'pose':
        this.listener.onPose(id, message.pose);
        this.broadcast({ t: 'pose', id, pose: message.pose }, id);
        break;
      case 'chat':
      case 'emote': {
        const now = Date.now();
        visitor.chatTimes = visitor.chatTimes.filter((t) => now - t < CHAT_WINDOW_MS);
        if (visitor.chatTimes.length >= CHAT_LIMIT) return;
        visitor.chatTimes.push(now);
        if (message.t === 'chat') {
          this.listener.onChat(id, message.text);
          this.broadcast({ t: 'chat', id, text: message.text }, id);
        } else {
          this.listener.onEmote(id, message.emote);
          this.broadcast({ t: 'emote', id, emote: message.emote }, id);
        }
        break;
      }
      case 'hello':
        break;
    }
  }

  private publishPlayers(): void {
    const players = this.players();
    this.listener.onPlayers(players);
    this.broadcast({ t: 'players', players });
  }

  private send(connection: Connection, message: HostMessage): void {
    connection.send(message);
  }

  private broadcast(message: HostMessage, exceptId?: string): void {
    if (this.closed && message.t !== 'kick') return;
    for (const visitor of this.visitors.values()) {
      if (visitor.info && visitor.connection.id !== exceptId) visitor.connection.send(message);
    }
  }
}

/**
 * Runs on a visitor's machine: mirrors the host's farm into the local
 * GameController and forwards the visitor's actions to the host.
 */
export class ClientSession {
  private closeReason: string | null = null;
  private welcomed = false;

  constructor(
    private readonly connection: Connection,
    private readonly game: GameController,
    private readonly listener: SessionListener,
  ) {}

  /** Says hello and resolves once the host has welcomed us. */
  start(name: string, color: string, onClosed: (reason: string) => void): Promise<PlayerInfo[]> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('The farm did not respond. Try again in a moment.'));
        this.connection.close();
      }, HELLO_TIMEOUT_MS);

      this.connection.onMessage((raw) => {
        const message = parseHostMessage(raw);
        if (!message) return;
        if (message.t === 'welcome') {
          clearTimeout(timer);
          this.welcomed = true;
          this.game.localPlayerId = message.you;
          this.game.setMode('client', (action) => this.sendAction(action));
          this.game.replaceState(message.state);
          this.listener.onPlayers(message.players);
          resolve(message.players);
          return;
        }
        if (message.t === 'kick') {
          this.closeReason = message.reason;
          if (!this.welcomed) {
            clearTimeout(timer);
            reject(new Error(message.reason));
          }
          return;
        }
        if (this.welcomed) this.handle(message);
      });

      this.connection.onClose(() => {
        clearTimeout(timer);
        if (!this.welcomed) {
          reject(new Error(this.closeReason ?? 'The connection closed.'));
          return;
        }
        onClosed(this.closeReason ?? 'Lost connection to the farm.');
      });

      this.send({ t: 'hello', v: PROTOCOL_VERSION, name, color });
    });
  }

  sendAction(action: GameAction): void {
    this.send({ t: 'action', action });
  }

  sendPose(pose: PlayerPose): void {
    this.send({ t: 'pose', pose });
  }

  sendChat(text: string): void {
    this.send({ t: 'chat', text });
  }

  sendEmote(emote: Emote): void {
    this.send({ t: 'emote', emote });
  }

  close(): void {
    this.closeReason = 'You left the farm.';
    this.connection.close();
  }

  private handle(message: HostMessage): void {
    switch (message.t) {
      case 'state':
        this.game.replaceState(message.state);
        break;
      case 'events':
        this.game.publish(message.events);
        break;
      case 'players':
        this.listener.onPlayers(message.players);
        break;
      case 'pose':
        if (message.id !== this.game.localPlayerId) this.listener.onPose(message.id, message.pose);
        break;
      case 'chat':
        this.listener.onChat(message.id, message.text);
        break;
      case 'emote':
        this.listener.onEmote(message.id, message.emote);
        break;
      case 'reject':
        this.game.rejected.emit(message.reason);
        break;
      case 'welcome':
      case 'kick':
        break;
    }
  }

  private send(message: ClientMessage): void {
    this.connection.send(message);
  }
}
